import { TextInputWithLabel } from '@/src/common/components/elements/Forms/TextInputWithLabel'
import { smarty } from '@/src/modules/smarty/Smarty'
import { styled } from '@/src/stitches.config'
import { BaseSyntheticEvent, useEffect, useRef, useState } from 'react'
import { IDeliveryStreetAddress } from '../../cart/ICart'

const SuggestionList = styled('ul', {
    borderRadius: '8px',
    border: '2px solid $primary',
    position: 'absolute',
    top: '100%',
    background: 'white',
    zIndex: 999,
    marginTop: '2px',
    padding: '0',
    overflowY: 'auto',
    maxWidth: '90vw',
})

const SuggestionListItem = styled('li', {
    listStyleType: 'none',
    padding: '8px',
    cursor: 'pointer',
    borderRadius: '8px',
    width: 'max-content',
    '&:hover': {
        background: '$light',
    },
    '&:focus': {
        background: '$light',
        outline: '2px solid $secondary',
    },
})

const SelectedAddress = styled('div', {
    borderRadius: '8px',
    border: '2px solid $darkest',
    background: '$white',
    p: 12,
    '>p ': {
        mt: 0,
        textDecoration: 'underline',
        fontWeight: 700,
        fontSize: '1.1em',
    },
})

interface ISmartyAddressSuggestion {
    streetLine: string
    secondary: string
    city: string
    state: string
    zipcode: string
    entries: number
}

const validateAddress = (address?: IDeliveryStreetAddress) => {
    return !!(address?.address1 && address.city && address.state && address.zip)
}

const formatSuggestionText = (addressSuggestion: ISmartyAddressSuggestion) => {
    const entriesDisplay =
        addressSuggestion.entries > 1
            ? ` (${addressSuggestion.entries} more entries) `
            : ''

    return `${addressSuggestion.streetLine} ${addressSuggestion.secondary} ${entriesDisplay} ${addressSuggestion.city}, ${addressSuggestion.state}, ${addressSuggestion.zipcode}`
}

const formatDeliveryStreetAddress = (address?: IDeliveryStreetAddress) => {
    if (!validateAddress(address)) return ''

    const { address1, address2, city, state, zip } = address!

    const street = address2 ? `${address1} ${address2}` : address1

    return `${street} ${city}, ${state}, ${zip}`
}

export default function SmartyAddressForm({
    value,
    error,
    onChange,
    toggleManualEntry,
    listMaxHeight = undefined,
}: {
    value?: IDeliveryStreetAddress
    error?: boolean
    onChange: (address: IDeliveryStreetAddress) => any
    toggleManualEntry?: (manualEntry: boolean) => any
    listMaxHeight?: number | string
}) {
    const [addressSuggestions, setAddressSuggestions] = useState<
        ISmartyAddressSuggestion[]
    >([])
    const [addressString, setAddressString] = useState(
        formatDeliveryStreetAddress(value)
    )
    const [completeAddress, setCompleteAddress] = useState<boolean>(
        validateAddress(value)
    )
    const [debounceTimer, setDebounceTimer] = useState<any>()
    const listElement = useRef<any>(null)

    const loadAddressSuggestions = (str: string) => {
        clearTimeout(debounceTimer)

        const timer = setTimeout(() => {
            smarty({ street: str }).then((suggestions) => {
                const suggestionsWithoutPOBox =
                    suggestions?.filter(
                        (item: any) => !item.streetLine.includes('PO Box')
                    ) ?? []

                setAddressSuggestions(suggestionsWithoutPOBox)
            })
        }, 300)

        setDebounceTimer(timer)
    }

    const selectSuggestion = (suggestion: ISmartyAddressSuggestion) => {
        const { streetLine, secondary, city, state, zipcode } = suggestion

        const street = secondary ? `${streetLine} ${secondary}` : streetLine

        const formatted = `${street} ${city}, ${state}, ${zipcode}`

        setAddressString(formatted)
        setCompleteAddress(true)

        onChange({
            address1: streetLine,
            address2: secondary,
            city,
            state,
            zip: zipcode,
        })

        setAddressSuggestions([])
    }

    useEffect(() => {
        setAddressString(formatDeliveryStreetAddress(value))
        setCompleteAddress(validateAddress(value))
    }, [value])

    useEffect(() => {
        const clickListener = (event: any) => {
            if (event?.target?.id !== 'smarty-input' && listElement.current) {
                setAddressSuggestions([])
            }
        }

        window.addEventListener('click', clickListener)

        return () => {
            window.removeEventListener('click', clickListener)
        }
    }, [])

    return (
        <label style={{ position: 'relative' }}>
            {completeAddress ? (
                <SelectedAddress>
                    <p>Selected Delivery Address:</p>
                    <span data-testid={'formatted-address'}>
                        {value?.address1}
                        <br />
                        {value?.address2 && (
                            <>
                                {value.address2}
                                <br />
                            </>
                        )}
                        {value?.city}
                        <br />
                        {value?.state}
                        <br />
                        {value?.zip}
                    </span>
                </SelectedAddress>
            ) : (
                <TextInputWithLabel
                    disabled={completeAddress}
                    value={addressString}
                    placeholder={'Search'}
                    label={'Street Address'}
                    id={'smarty-input'}
                    style={{ cursor: completeAddress ? 'default' : 'text' }}
                    hasError={error}
                    onChange={(e: BaseSyntheticEvent) => {
                        setAddressString(e.target.value)
                        loadAddressSuggestions(e.target.value)
                    }}
                    onKeyDown={(event: any) => {
                        if (event.key === 'ArrowDown') {
                            if (listElement?.current?.children?.length) {
                                listElement.current.children[0].focus()
                            }
                        }
                    }}
                />
            )}
            {/* @todo refactor to use reusable select component*/}
            {!!addressSuggestions?.length && (
                <SuggestionList
                    ref={listElement}
                    style={{ maxHeight: listMaxHeight }}
                >
                    {addressSuggestions.map((suggestion) => (
                        <SuggestionListItem
                            key={JSON.stringify(suggestion)}
                            tabIndex={0}
                            onClick={() => selectSuggestion(suggestion)}
                            onKeyDown={(event) => {
                                if (event.key == 'Enter') {
                                    selectSuggestion(suggestion)
                                }

                                if (event.key === 'ArrowDown') {
                                    if (event.currentTarget.nextSibling) {
                                        // @ts-ignore
                                        event.currentTarget.nextSibling.focus()
                                    }
                                }

                                if (event.key === 'ArrowUp') {
                                    if (event.currentTarget.previousSibling) {
                                        // @ts-ignore
                                        event.currentTarget.previousSibling.focus()
                                    }
                                }
                            }}
                        >
                            {formatSuggestionText(suggestion)}
                        </SuggestionListItem>
                    ))}

                    {/* Add a link after the suggestions to switch to manual entry */}
                    {toggleManualEntry && (
                        <SuggestionListItem
                            style={{
                                color: 'var(--colors-secondary',
                                textDecoration: 'underline',
                            }}
                            tabIndex={0}
                            onClick={() => toggleManualEntry(true)}
                            onKeyDown={(event) => {
                                if (event?.key == 'Enter') {
                                    toggleManualEntry(true)
                                }

                                if (event.key === 'ArrowUp') {
                                    if (
                                        listElement?.current?.children?.length
                                    ) {
                                        listElement.current.children[
                                            listElement.current.children
                                                .length - 2
                                        ].focus()
                                    }
                                }
                            }}
                        >
                            Didn&#39;t find the right address? Enter it
                            manually!
                        </SuggestionListItem>
                    )}
                </SuggestionList>
            )}
        </label>
    )
}
